import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import style from './Calendar.module.css';
import CalendarHeader from './CalendarHeader/CalendarHeader';
import DatesGrid from './DatesGrid/DatesGrid';
import DaysOfWeek from './DaysOfWeek/DaysOfWeek';
import Modal from './TimeModal/TimeModal';
import {
  applyTemplate,
  clearMonth,
  fetchUpdatedSchedule,
  saveSchedule,
} from './helpers';

const TPL_DAYS = [
  { label: 'Пн', dow: 1 },
  { label: 'Вт', dow: 2 },
  { label: 'Ср', dow: 3 },
  { label: 'Чт', dow: 4 },
  { label: 'Пт', dow: 5 },
  { label: 'Сб', dow: 6 },
  { label: 'Нд', dow: 0 },
];

interface CalendarProps {
  userId: string;
  updateCounter: number;
  setUpdateCounter: React.Dispatch<React.SetStateAction<number>>;
}

interface ModalPosition {
  top: number;
  left: number;
}

const Calendar: React.FC<CalendarProps> = ({
  userId,
  updateCounter,
  setUpdateCounter,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [modalPosition, setModalPosition] = useState<ModalPosition>({
    top: 0,
    left: 0,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const schedule = useSelector((state: RootState) => state.schedule.schedule);

  const [tpl, setTpl] = useState<
    Record<number, { on: boolean; start: string; finish: string }>
  >({
    1: { on: true, start: '08:00', finish: '23:00' },
    2: { on: true, start: '08:00', finish: '23:00' },
    3: { on: true, start: '08:00', finish: '23:00' },
    4: { on: true, start: '08:00', finish: '23:00' },
    5: { on: true, start: '08:00', finish: '23:00' },
    6: { on: true, start: '09:00', finish: '21:00' },
    0: { on: false, start: '09:00', finish: '21:00' },
  });
  const [applying, setApplying] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [timesOpen, setTimesOpen] = useState(false);

  const scheduledDates = useMemo(() => {
    const set = new Set<string>();
    Object.values(schedule || {}).forEach((arr: any) => {
      (arr || []).forEach((it: any) => {
        if (it?.startAt)
          set.add(new Date(it.startAt).toISOString().split('T')[0]);
      });
    });
    return set;
  }, [schedule]);

  const setDay = (
    dow: number,
    patch: Partial<{ on: boolean; start: string; finish: string }>,
  ) => setTpl((p) => ({ ...p, [dow]: { ...p[dow], ...patch } }));

  const handleApplyTemplate = async () => {
    setApplying(true);
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const entries: { date: Date; startAt: string; finishAt: string }[] = [];
    for (let d = 1; d <= daysInMonth; d += 1) {
      const date = new Date(Date.UTC(year, month, d));
      const dow = date.getUTCDay();
      const cfg = tpl[dow];
      if (!cfg?.on) continue;
      const dateStr = date.toISOString().split('T')[0];
      if (scheduledDates.has(dateStr)) continue;
      entries.push({ date, startAt: cfg.start, finishAt: cfg.finish });
    }
    if (entries.length) {
      await applyTemplate(userId, entries, fetchUpdatedSchedule, dispatch);
    }
    setApplying(false);
  };

  const handleClearMonth = async () => {
    setClearing(true);
    await clearMonth(
      userId,
      schedule,
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      fetchUpdatedSchedule,
      dispatch,
    );
    setClearing(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  const openModal = (
    date: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setSelectedDate(date);
    setModalOpen(true);
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetTop = 130;
    const offsetLeft = 50;
    setModalPosition({
      top: rect.top + window.scrollY + offsetTop,
      left: rect.left + window.scrollX + offsetLeft,
    });
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedDate('');
  };

  const handleSave = async (startAt: string, finishAt: string) => {
    await saveSchedule(
      userId,
      selectedDate,
      startAt,
      finishAt,
      fetchUpdatedSchedule,
      dispatch,
    );
    closeModal();
  };

  return (
    <div className={style.calendar}>
      <CalendarHeader
        currentMonth={currentMonth}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
      />
      <div className={style.templatePanel}>
        <div className={style.tplRow7}>
          {TPL_DAYS.map((d) => {
            const cfg = tpl[d.dow];
            return (
              <button
                key={d.dow}
                type="button"
                className={`${style.tplToggle} ${
                  cfg.on ? style.tplToggleOn : ''
                }`}
                onClick={() => setDay(d.dow, { on: !cfg.on })}
              >
                {d.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className={style.tplTimesToggle}
          onClick={() => setTimesOpen((o) => !o)}
        >
          {timesOpen ? 'Сховати час ▴' : 'Налаштувати час ▾'}
        </button>
        {timesOpen && (
          <>
            <div className={style.tplRow7}>
              {TPL_DAYS.map((d) => {
                const cfg = tpl[d.dow];
                return (
                  <input
                    key={d.dow}
                    type="time"
                    className={`${style.tplTime} ${
                      cfg.on ? '' : style.tplTimeOff
                    }`}
                    value={cfg.start}
                    disabled={!cfg.on}
                    onChange={(e) => setDay(d.dow, { start: e.target.value })}
                  />
                );
              })}
            </div>
            <div className={style.tplRow7}>
              {TPL_DAYS.map((d) => {
                const cfg = tpl[d.dow];
                return (
                  <input
                    key={d.dow}
                    type="time"
                    className={`${style.tplTime} ${
                      cfg.on ? '' : style.tplTimeOff
                    }`}
                    value={cfg.finish}
                    disabled={!cfg.on}
                    onChange={(e) => setDay(d.dow, { finish: e.target.value })}
                  />
                );
              })}
            </div>
          </>
        )}
        <div className={style.tplActions}>
          <button
            type="button"
            className={style.tplClear}
            onClick={handleClearMonth}
            disabled={clearing}
          >
            {clearing ? 'Очищення…' : 'Очистити місяць'}
          </button>
          <button
            type="button"
            className={style.tplApply}
            onClick={handleApplyTemplate}
            disabled={applying}
          >
            {applying ? 'Заповнення…' : 'Заповнити за шаблоном'}
          </button>
        </div>
      </div>
      <DaysOfWeek />
      <DatesGrid
        currentMonth={currentMonth}
        onDateClick={openModal}
        schedule={schedule}
        userId={userId}
        setUpdateCounter={setUpdateCounter}
      />
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        position={modalPosition}
      />
    </div>
  );
};

export default Calendar;
